import { html } from "core/Controls/BookSearchLower.html";
import { Misc, LanguageCodes } from "core/Misc";
import { Tabset } from "core/Controls/Tabset";
import { FormState } from "core/FormState";
import { range } from "shared/functions";
import { UserLangGradeShort } from "shared/LanguageExtensions";
import { BookSearchField, BookSearchLength, BookSearchQuery } from "core/Services/SearchBooksService";
import { View, ViewData, CollectionView, EventListener, Event } from "silver";
import { BooksLang, UserLang } from "Lang";

import {
	FiltersUpdatedEvent,
	BookSearchInterface,
	ButtonInputViewData,
	optionArray,
	isCheckbox, LanguageButtonInputViewData
} from "core/Controls/BookSearch";

export interface ButtonAndIconInputViewData<ID = string> extends ButtonInputViewData<ID> {
	icon: string;
}

interface FilterButtonViewData extends ViewData {
	filter: string;
	buttonClass: string;
	iconClass: string;
	label: string;
}

interface ButtonInputView extends View<ButtonInputViewData> {
	input: HTMLInputElement;
}

interface BookSearchLowerViewData {
	lang: BooksLang;
	searchFields: Array<ButtonAndIconInputViewData<BookSearchField>>;
	lengthBtns: Array<ButtonAndIconInputViewData<BookSearchLength>>;
	audioBtns: ButtonAndIconInputViewData[];
	lexileToggles: ButtonInputViewData[];
	gradeLevels: ButtonInputViewData[];
	atosToggles: ButtonInputViewData[];
	guidedReadingRow1: ButtonInputViewData[];
	guidedReadingRow2: ButtonInputViewData[];
	languageToggles: LanguageButtonInputViewData[];
	filterButtons: FilterButtonViewData[];
}

interface SearchFieldView extends View<ButtonAndIconInputViewData<BookSearchField>> {
	checkbox: HTMLInputElement;
}

interface BookSearchLowerView extends View<BookSearchLowerViewData> {
	formEl: HTMLFormElement;
	tabset: HTMLElement;
	tablist: HTMLElement;
	tabpanelContainer: HTMLElement;
	arrow: HTMLDivElement;
	lexileToggles: CollectionView<ButtonInputViewData, ButtonInputView>;
	gradeLevels: CollectionView<ButtonInputViewData, ButtonInputView>;
	gradeSuggestion: HTMLDivElement;
	buttonContainer: HTMLDivElement;
	filterButtons: CollectionView<FilterButtonViewData>;
	searchFields: CollectionView<ButtonAndIconInputViewData<BookSearchField>, SearchFieldView>;
	atosToggles: CollectionView<ButtonInputViewData>;
	guidedReadingRow1: CollectionView<ButtonInputViewData>;
	guidedReadingRow2: CollectionView<ButtonInputViewData>;
	lengthBtns: CollectionView<ButtonAndIconInputViewData<BookSearchLength>>;
	audioBtns: CollectionView<ButtonAndIconInputViewData>;
	languageToggles: CollectionView<LanguageButtonInputViewData>;

	filterBtnFilter: HTMLDivElement;
	filterPanelFilter: HTMLDivElement;
	filterBtnFiction: HTMLDivElement;
	filterPanelFiction: HTMLDivElement;
	filterBtnLexile: HTMLDivElement;
	filterPanelLexile: HTMLDivElement;
	filterBtnAtos: HTMLDivElement;
	filterPanelAtos: HTMLDivElement;
	filterBtnReading: HTMLDivElement;
	filterPanelReading: HTMLDivElement;
	filterBtnGrade: HTMLDivElement;
	filterPanelGrade: HTMLDivElement;
	filterBtnLength: HTMLDivElement;
	filterPanelLength: HTMLDivElement;
	filterBtnLanguage: HTMLDivElement;
	filterPanelLanguage: HTMLDivElement;
}

enum TabId {
	SEARCH = "search",
	FICTION = "fiction",
	LEXILE = "lexile",
	ATOS = "atos",
	GUIDED_READING = "guidedReading",
	GRADE_LEVEL = "gradeLevel",
	LENGTH = "length",
	LANGUAGE = "language"
}

export class BookSearchLower implements BookSearchInterface {

	public readonly onChange = EventListener.create<Event>("change");
	public readonly onFormSubmit = EventListener.create<Event>("formSubmit");
	public readonly onFiltersUpdated = EventListener.create<FiltersUpdatedEvent>("filtersUpdated");

	private readonly view: BookSearchLowerView;
	private readonly formState: FormState;

	private tabset: Tabset;

	public state(): FormState {
		return this.formState;
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	constructor(
		private lang: BooksLang,
		private userLang: UserLang,
		availableLanguages: number[]
	) {
		this.view = new View<BookSearchLowerViewData>(html, {
			getFirstLetter: (value: string): string => {
				return value.charAt(0).toUpperCase();
			},
			handleResetButtonClick: () => {
				this.formState.restore();
				return false;
			},
			handleRadioLabelClick: (e: MouseEvent) => {
				const input = document.getElementById((e.currentTarget as HTMLElement).getAttribute("for") as string) as HTMLInputElement;
				if (input.checked) {
					input.checked = false;
					return false;
				}
			},
			languageCode: (languageId: number): string => {
				if (!LanguageCodes.hasOwnProperty(languageId)) {
					return "";
				}
				return LanguageCodes[languageId];
			}
		}, {
			lang,
			filterButtons: [
				{ filter: "search", buttonClass: "filter-btn-filter", iconClass: "fIcon -ll-filter", label: lang.LABEL_INCLUDE() },
				{ filter: "fiction", buttonClass: "filter-btn-fiction", iconClass: "fIcon -ll-fnf", label: lang.LABEL_FICTION() },
				{ filter: "lexile", buttonClass: "filter-btn-lexile", iconClass: "fIcon -ll-lexile", label: lang.LABEL_LEXILE() },
				{ filter: "atos", buttonClass: "filter-btn-atos", iconClass: "fIcon -ll-atos", label: lang.LABEL_ATOS() },
				{ filter: "guidedReading", buttonClass: "filter-btn-guided-reading", iconClass: "fIcon -ll-guidedReading", label: lang.LABEL_GUIDED_READING() },
				{ filter: "gradeLevel", buttonClass: "filter-btn-grade-level", iconClass: "fIcon -ll-gradeLevel", label: lang.LABEL_GRADE_LEVEL() },
				{ filter: "length", buttonClass: "filter-btn-story-length", iconClass: "fIcon -ll-storyLength", label: lang.LABEL_STORY_TYPE() },
				{ filter: "language", buttonClass: "filter-btn-language", iconClass: "fIcon -ll-lang", label: lang.LABEL_LANGUAGE() }
			],
			searchFields: [
				{ id: "series", label: this.lang.FILTER_SERIES(), icon: "fIcon -ll-search-series" },
				{ id: "title", label: this.lang.FILTER_TITLE(), icon: "fIcon -ll-search-title" },
				{ id: "author", label: this.lang.FILTER_AUTHOR(), icon: "fIcon -ll-search-author" },
				{ id: "description", label: this.lang.FILTER_DESCRIPTION(), icon: "fIcon -ll-search-descr" },
				{ id: "publisher", label: this.lang.FILTER_PUBLISHER(), icon: "fIcon -ll-search-publisher" }
			],
			lexileToggles: [
				{ id: "lt100", label: "-" },
				...optionArray(range(100, 1000, 100)),
				{ id: "gt1100", label: "+" }
			],
			gradeLevels: [
				{ id: "1", label: "-" },
				...optionArray(range(2, 11, 1), v => UserLangGradeShort(this.userLang, v)),
				{ id: "gt12", label: "+" }
			],
			atosToggles: [
				...optionArray(range(0, 9)),
				{ id: "10", label: "10+" }
			],
			lengthBtns: [
				{ id: "short", label: this.lang.FILTER_SHORT(), icon: "fIcon -ll-length-short" },
				{ id: "medium", label: this.lang.FILTER_MEDIUM(), icon: "fIcon -ll-length-medium" },
				{ id: "long", label: this.lang.FILTER_LONG(), icon: "fIcon -ll-length-long" },
				{ id: "epic", label: this.lang.FILTER_EPIC(), icon: "fIcon -ll-length-epic" }
			],
			audioBtns: [
				{ id: "yes", label: this.lang.FILTER_YES(), icon: "fIcon -ll-audio-yes" },
				{ id: "no", label: this.lang.FILTER_NO(), icon: "fIcon -ll-audio-no" }
			],
			guidedReadingRow1: optionArray(range(0, 12), Misc.guidedReadingLevel),
			guidedReadingRow2: optionArray(range(13, 25), Misc.guidedReadingLevel),
			languageToggles:   [
				...availableLanguages.map(langId => ({
					id:    String(langId),
					label: Misc.languageTitle(langId),
					lang:  LanguageCodes[langId] || ""
				})),
				{ id: "bi", label: this.lang.FILTER_BILINGUAL(), lang: document.documentElement.lang }
			]
		}) as BookSearchLowerView;
		this.formState = new FormState(this.view.formEl);
		this.view.formEl.addEventListener("submit", (e) => {
			e.preventDefault();
			this.onFormSubmit.trigger({ });
		});
		this.tabset = new Tabset(this.view.tabset, this.view.tablist, this.view.tabpanelContainer);

		this.tabset.onTabActivate((e) => {
			e.tab.tab.classList.add("-active");
			e.tab.tabpanel.classList.remove("-hidden");
			this.tabset.element.setAttribute("data-filter", e.tab.tabId);
		});
		this.tabset.onTabDeactivate((e) => {
			e.tab.tab.classList.remove("-active");
			e.tab.tabpanel.classList.add("-hidden");
		});

		this.registerTab(TabId.SEARCH, this.view.filterBtnFilter, this.view.filterPanelFilter);
		this.registerTab(TabId.FICTION, this.view.filterBtnFiction, this.view.filterPanelFiction);
		this.registerTab(TabId.LEXILE, this.view.filterBtnLexile, this.view.filterPanelLexile);
		this.registerTab(TabId.ATOS, this.view.filterBtnAtos, this.view.filterPanelAtos);
		this.registerTab(TabId.GUIDED_READING, this.view.filterBtnReading, this.view.filterPanelReading);
		this.registerTab(TabId.GRADE_LEVEL, this.view.filterBtnGrade, this.view.filterPanelGrade);
		this.registerTab(TabId.LENGTH, this.view.filterBtnLength, this.view.filterPanelLength);
		this.registerTab(TabId.LANGUAGE, this.view.filterBtnLanguage, this.view.filterPanelLanguage);

		this.view.formEl.appendChild(this.tabset.element);

		this.init();

		this.setActiveFilter(TabId.SEARCH);
	}

	private registerTab(tabId: string, tab: HTMLElement, tabpanel: HTMLElement): void {
		this.tabset.add({ tabId, tab, tabpanel });
	}

	public import(data: BookSearchQuery): void {
		let inputs, input, i;
		if (typeof data.guidedReading !== "undefined") {
			input = this.view.element.querySelector("input[name=\"guidedReading\"][value=\"" + (parseInt(data.guidedReading[ 0 ], 36) - 10) + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		if (typeof data.atos !== "undefined") {
			input = this.view.element.querySelector("input[name=\"atos\"][value=\"" + (data.atos[ 0 ]) + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		if (typeof data.search !== "undefined") {
			inputs = this.view.element.querySelectorAll("input[name=\"search[]\"]") as NodeListOf<HTMLInputElement>;
			for (i = 0; i < inputs.length; i++) {
				inputs[ i ].checked = data.search.indexOf(inputs[ i ].value as BookSearchField) !== -1;
			}
		}
		if (typeof data.language !== "undefined") {
			inputs = this.view.element.querySelectorAll("input[name=\"language[]\"]") as NodeListOf<HTMLInputElement>;
			for (i = 0; i < inputs.length; i++) {
				inputs[ i ].checked = data.language.indexOf(inputs[ i ].value) !== -1;
			}
		}
		if (typeof data.audio !== "undefined") {
			input = this.view.element.querySelector("input[name=\"audio[]\"][value=\"" + (data.audio ? "yes" : "no") + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		if (typeof data.lexile !== "undefined") {
			const value = (data.lexile[ 0 ] == 0 ? "lt100" :
				(data.lexile[ 0 ] == 1100 ? "gt1100" : data.lexile[ 0 ]));

			input = this.view.element.querySelector("input[name=\"lexile\"][value=\"" + value + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		if (typeof data.length !== "undefined") {
			input = this.view.element.querySelector("input[name=\"length\"][value=\"" + (data.length) + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}
		if (typeof data.fiction !== "undefined") {
			inputs = this.view.element.querySelectorAll("input[name=\"fiction[]\"]") as NodeListOf<HTMLInputElement>;
			for (i = 0; i < inputs.length; i++) {
				inputs[ i ].checked = (Number(data.fiction) == Number(inputs[ i ].value));
			}
		}
		if (typeof data.gradeLevel !== "undefined") {
			input = this.view.element.querySelector("input[name=\"gradeLevel\"][value=\"" + (data.gradeLevel[ 0 ] == 12 ? "gt12" : data.gradeLevel[ 0 ]) + "\"]") as HTMLInputElement|null;
			if (input) {
				input.checked = true;
			}
		}

		this.formState.doGroupChange();
	}

	public export(): BookSearchQuery {
		const data = this.formState.export();
		const dirtyFields = this.formState.listDirty() as Array<keyof BookSearchQuery>;
		const out: any = { };

		if (data.guidedReading) {
			const current = parseInt(data.guidedReading, 10);
			data.guidedReading = [
				Misc.guidedReadingLevel(current),
				Misc.guidedReadingLevel(current)
			];
		}

		if (data.gradeLevel && data.gradeLevel == "gt12") {
			data.gradeLevel = [12, 14];
		} else if (data.gradeLevel) {
			const current = parseInt(data.gradeLevel, 10);
			data.gradeLevel = [current, current + 1];
		}

		if (data.lexile == "lt100") {
			data.lexile = [0, 100];
		} else if (data.lexile == "gt1100") {
			data.lexile = [ 1100, 1500];
		} else if (data.lexile) {
			const current = parseInt(data.lexile, 10);
			data.lexile = [current, current + 100];
		}

		if (data.atos == "0") {
			data.atos = [0, 0.9];
		} else if (data.atos == "10") {
			data.atos = [10, 16.9];
		} else if (data.atos) {
			const current = parseInt(data.atos, 10);
			data.atos = [current, current + 0.9];
		}

		for (const i in dirtyFields) {
			if (dirtyFields.hasOwnProperty(i) && data.hasOwnProperty(i)) {
				out[i] = data[i];
			}
		}
		if (data.fiction && data.fiction.length == 2) {
			delete out.fiction;
		}
		if (data.fiction && data.fiction.length != 2) {
			out.fiction = (data.fiction[ 0 ] == 1);
		}
		if (data.audio && data.audio.length === 1) {
			out.audio = (data.audio[0] == "yes" ? 1 : 0);
		}

		if (data.language) {
			out.language = data.language;
		}

		if (data.search) {
			out.search = data.search;
		}
		return out;
	}

	public resetField(fieldName: string): void {
		const inputs = this.view.element.querySelectorAll('[name^="' + fieldName + '"]');

		for (let i = 0; i < inputs.length; i++) {
			const input = inputs[i] as HTMLInputElement;
			if (isCheckbox(input)) {
				input.checked = input.defaultChecked;
			} else {
				input.value = input.defaultValue;
			}
		}

		this.formState.doGroupChange();
	}

	public suggestGradeLevel(userGradeLevel: number): void {
		const target = this.view.gradeLevels.get(userGradeLevel >= 12 ? "gt12" : String(userGradeLevel - 1));
		target.element.appendChild(this.view.gradeSuggestion);
		target.input.setAttribute("aria-describedby", "grade-suggestion");
		this.view.gradeSuggestion.style.display = "";
	}

	private init(): void {
		let lastDirty: string|null = null;
		this.formState.onChange(() => {
			const dirtyInputs = this.formState.listDirty();
			const out: Array<keyof BookSearchQuery> = [];
			for (const name in dirtyInputs) {
				if (dirtyInputs.hasOwnProperty(name)) {
					out.push(name as keyof BookSearchQuery);
				}
			}
			const dirty = JSON.stringify(out);
			if (dirty != lastDirty) {
				lastDirty = dirty;
				this.onFiltersUpdated.trigger({
					filters: out
				});
			}
			this.onChange.trigger({ });
		});
	}

	private setActiveFilter(filter: string): void {
		this.tabset.showTab(filter, false);
	}

	public selectSearchFields(searchFields: BookSearchField[]): void {
		this.view.searchFields.forEach(view => {
			view.checkbox.checked = (searchFields.indexOf(view.dataset.id) >= 0);
		});
	}
}
